import { cn } from "@nt/ui/cn";
import { buttonVariants } from "@nt/ui/recipes";
import { Check, Copy } from "lucide-react";
import { useId, type Ref } from "react";

import { useCopy } from "@/components/copy-button";

/**
 * A read-only monospace field with its copy button inside, for links and
 * codes. `label` names the field (shown above unless `hideLabel`) and the
 * button ("Copy short link").
 */
export function CopyField({
  value,
  label,
  hideLabel = false,
  compact = false,
  copyRef,
  className,
}: {
  value: string;
  label: string;
  hideLabel?: boolean;
  /** A shorter field for table rows. */
  compact?: boolean;
  /** The copy button, e.g. to focus it after a link is created. */
  copyRef?: Ref<HTMLButtonElement>;
  className?: string;
}) {
  const id = useId();
  const [state, copy] = useCopy(value);
  const said =
    state === "done"
      ? "Copied"
      : state === "failed"
        ? "Select and copy by hand"
        : "Copy";
  return (
    <div className={cn("flex min-w-0 flex-col gap-1.5", className)}>
      <label
        htmlFor={id}
        className={cn("text-sm text-muted-foreground", hideLabel && "sr-only")}
      >
        {label}
      </label>
      <div
        className={cn(
          "flex min-w-0 items-center gap-1 rounded-control border border-input bg-card pl-3 pr-1",
          compact ? "h-9" : "h-control",
        )}
      >
        <input
          id={id}
          readOnly
          value={value}
          onFocus={(e) => e.currentTarget.select()}
          className="min-w-0 flex-1 truncate bg-transparent font-mono text-sm text-foreground outline-none"
        />
        <button
          ref={copyRef}
          type="button"
          onClick={copy}
          aria-label={state === "idle" ? `Copy ${label.toLowerCase()}` : said}
          className={buttonVariants({
            variant: "ghost",
            size: compact ? "xs" : "sm",
          })}
        >
          {state === "done" ? <Check aria-hidden /> : <Copy aria-hidden />}
          <span aria-live="polite" className={cn(compact && "sr-only")}>
            {said}
          </span>
        </button>
      </div>
    </div>
  );
}
